import * as Dialog from '@radix-ui/react-dialog';
import { XIcon, LinkedinIcon } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import linkedInService from 'features/social-media/services/linkedinApi';
import twitterService from 'features/social-media/services/twitterApi';
import { Button } from 'components/ui/button';
import { ISocialMediaContext } from 'features/social-media/types';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { connectSocialPlatform } from 'store/slices/authSlice';
import toast from 'react-hot-toast';

interface SocialMediaDialogProps {
  isOpen: boolean;
  onClose: () => void;
  platform: 'x' | 'linkedin';
  socialMediaContent: ISocialMediaContext[] | null;
}

const SocialMediaDialog = ({
  isOpen,
  onClose,
  platform,
  socialMediaContent,
}: SocialMediaDialogProps) => {
  const dispatch = useDispatch();

  useEffect(() => {
    // Listen for messages from the popup window
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'LINKEDIN_AUTH_SUCCESS') {
        const { linkedin_access_token, linkedin_social_user_id } =
          event.data.data;

        // Update Redux state with LinkedIn connection
        dispatch(
          connectSocialPlatform({
            platform: 'linkedin',
            token: linkedin_access_token,
            username: linkedin_social_user_id,
            expiresAt: Date.now() + 60 * 24 * 60 * 60 * 1000, // 60 days expiry
          })
        );

        toast.success('LinkedIn connected successfully');
        onClose();
      } else if (event.data?.type === 'TWITTER_AUTH_SUCCESS') {
        const { twitter_access_token, twitter_social_user_id } = event.data.data;

        // Update Redux state with Twitter connection
        dispatch(
          connectSocialPlatform({
            platform: 'x',
            token: twitter_access_token,
            username: twitter_social_user_id,
            expiresAt: Date.now() + 60 * 24 * 60 * 60 * 1000,
          })
        );

        toast.success('X connected successfully');
        onClose();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [dispatch, onClose]);

  const saveSocialMediaContent = () => {
    localStorage.setItem(
      'socialMediaContent',
      JSON.stringify(socialMediaContent)
    );
  };

  const x = useMutation({
    mutationKey: ['initiate-twitter-auth'],
    mutationFn: twitterService.getAuthUrl,
    onSuccess: ({ data }) => {
      saveSocialMediaContent();
      window.open(data.url, '_blank');
      onClose();
    },
  });

  const linkedIn = useMutation({
    mutationKey: ['initiate-linkedIn-auth'],
    mutationFn: linkedInService.getAuthUrl,
    onSuccess: ({ data }) => {
      saveSocialMediaContent();
      window.open(data.url, '_blank');
      onClose();
    },
  });

  const platformConfig = {
    x: {
      title: 'X access required',
      message:
        'You need to connect your X account to schedule or publish the post.',
      buttonLabel: 'Add X account',
      icon: <XIcon className="h-4 w-4" />,
      buttonClass: 'bg-black text-sm text-white hover:bg-black/90',
      handleConnect: () => x.mutate(),
      isLoading: x.isPending,
    },
    linkedin: {
      title: 'LinkedIn access required',
      message:
        'You need to connect your LinkedIn account to schedule or publish the post.',
      buttonLabel: 'Add LinkedIn account',
      icon: <LinkedinIcon className="h-4 w-4" />,
      buttonClass: 'bg-[#0A66C2] text-sm text-white hover:bg-[#0A66C2]/90',
      handleConnect: () => linkedIn.mutate(),
      isLoading: linkedIn.isPending,
    },
  };

  const config = platformConfig[platform];

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/20 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg w-full max-w-[340px]">
          <Dialog.Title className="text-lg font-semibold text-slate-900">
            {config.title}
          </Dialog.Title>

          <Dialog.Description className="mt-2 text-sm text-slate-600">
            {config.message}
          </Dialog.Description>

          <div className="mt-6 flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-slate-500 text-sm hover:text-slate-600"
              disabled={config.isLoading}
            >
              Cancel
            </Button>

            <Button
              size="sm"
              onClick={config.handleConnect}
              disabled={config.isLoading}
              className={`${config.buttonClass} flex items-center gap-2`}
            >
              {config.icon}
              {config.isLoading ? 'Connecting...' : config.buttonLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default SocialMediaDialog;
